import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { ChevronRight, AlertTriangle } from 'lucide-react';
import { gameData, mockApiCalls } from '../data/mock';
import './MissionBriefing.css';
import explorerSilhouette from '../assets/instruction_page/human_black_2.png';
import backgroundImage from '../assets/instruction_page/background_img.jpg'

const MissionBriefing = () => {
  const navigate = useNavigate();
  const [visibleInstructions, setVisibleInstructions] = useState(0);
  const [backgroundLoaded, setBackgroundLoaded] = useState(false);
  const [silhouetteLoaded, setSilhouetteLoaded] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setVisibleInstructions((prev) => {
        if (prev < gameData.instructions.length) {
          return prev + 1;
        }
        clearInterval(interval);
        return prev;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const handleNext = () => {
    navigate('/map');
  };

  const isLoading = !backgroundLoaded || !silhouetteLoaded;

  return (
    <div className="min-h-screen relative">
      {/* Spinner shown while images are fetching */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black text-white z-50">
          <div className="flex items-center gap-3 text-lg font-semibold">
            <svg className="animate-spin h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Loading mission...
          </div>
        </div>
      )}

      {/*
        Always rendered so each image makes exactly one HTTP request.
        visibility:hidden keeps the content invisible while loading without
        removing these elements from the DOM (display:none would cause a
        second fetch when visibility is restored).
      */}
      <div
        className="min-h-screen relative overflow-hidden flex items-center justify-center p-4"
        style={{ visibility: isLoading ? 'hidden' : 'visible' }}
      >
        {/* Background as <img> so onLoad fires on the same element that displays it */}
        <img
          src={backgroundImage}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover"
          onLoad={() => setBackgroundLoaded(true)}
          onError={() => setBackgroundLoaded(true)}
        />

      <div className="absolute inset-0 mission-briefing-overlay"></div>

      <div className="absolute bottom-0 left-0 w-1/3 h-2/3 opacity-100 hidden lg:block">
        <img
          src={explorerSilhouette}
          alt="Explorer Silhouette"
          className="w-full h-full object-contain object-bottom"
          onLoad={() => setSilhouetteLoaded(true)}
          onError={() => setSilhouetteLoaded(true)}
        />
      </div>

      <div className="absolute bottom-8 lg:bottom-12 xl:bottom-16 2xl:bottom-20 left-1/2 -translate-x-1/2 z-20 flex justify-end w-full max-w-4xl lg:w-2/3 px-4">
        <Button
          onClick={handleNext}
          size="sm"
          className="btn-proceed font-semibold px-5 py-3 text-sm rounded-md"
        >
          Proceed
          <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>

      <div className="relative z-10 w-full max-w-4xl flex flex-col items-center">
        <div className="text-center mb-2 w-full lg:w-2/3">
            <AlertTriangle className="w-12 h-12 text-orange-400 mx-auto mb-4" />
            <h2 className="text-4xl md:text-5xl font-bold uppercase mission-title">
              Mission Briefing
            </h2>
            <div className="mission-divider mx-auto mt-2"></div>
        </div>

        <div className="w-full lg:w-2/3 mission-panel p-8 mb-16">
          <div className="space-y-3 mission-list">
            {gameData.instructions.slice(0, visibleInstructions).map((instruction, index) => (
              <div key={index} className="flex items-start gap-3 animate-fade-in">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-700 flex items-center justify-center text-white font-semibold">
                  {index + 1}
                </div>
                <p className="text-base md:text-lg text-gray-200 flex-1">
                  {instruction}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default MissionBriefing;
